import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  confirmJaneGoldUser,
  createAeropayUser,
  deleteAeropayBankAccount,
  linkAeropayBankAccount,
  selectAeropayBankAccount,
} from './aeropay.api';
import { aeropayQueryKeys } from './aeropay.queries';
import type {
  AeropayUser,
  BankAccountId,
  CreateAeropayUser,
  LinkBankAccountProps,
  UserId,
} from './aeropay.types';

interface MutationProps extends UserId {
  jwt?: string | null;
}

export const useConfirmJaneGoldUser = ({ janeUserId, jwt }: MutationProps) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => confirmJaneGoldUser({ jwt }),
    onSuccess: () => {
      queryClient.setQueryData<AeropayUser | null>(
        aeropayQueryKeys.aeropayUser({ janeUserId }),
        (data) => (data ? { ...data, requires_gold_confirmation: false } : data)
      );
    },
  });
};

export const useCreateAeropayUser = ({ janeUserId, jwt }: MutationProps) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAeropayUser) => createAeropayUser({ data, jwt }),
    onSuccess: (data) => {
      queryClient.setQueryData<AeropayUser | null>(
        aeropayQueryKeys.aeropayUser({ janeUserId }),
        () => data
      );
    },
  });
};

export const useLinkAeropayBankAccount = ({
  janeUserId,
  jwt,
}: MutationProps) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LinkBankAccountProps) =>
      linkAeropayBankAccount({ data, jwt }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: aeropayQueryKeys.aeropayUser({ janeUserId }),
      }),
  });
};

interface UseSelectAeropayBankAccountProps extends MutationProps {
  onMutationSuccess?: () => void;
}

export const useSelectAeropayBankAccount = ({
  janeUserId,
  jwt,
  onMutationSuccess,
}: UseSelectAeropayBankAccountProps) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BankAccountId) =>
      selectAeropayBankAccount({ data, jwt }),
    onSuccess: (_, variables) => {
      queryClient.setQueryData<AeropayUser | null>(
        aeropayQueryKeys.aeropayUser({ janeUserId }),
        (data) =>
          data
            ? {
                ...data,
                bank_accounts: data.bank_accounts.map((bankAccount) => ({
                  ...bankAccount,
                  is_selected:
                    bankAccount.bank_account_id === variables.bankAccountId,
                })),
              }
            : data
      );
      onMutationSuccess?.();
    },
  });
};

export const useDeleteAeropayBankAccount = ({
  janeUserId,
  jwt,
}: MutationProps) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BankAccountId) =>
      deleteAeropayBankAccount({ data, jwt }),
    onSuccess: (_, variables) => {
      queryClient.setQueryData<AeropayUser | null>(
        aeropayQueryKeys.aeropayUser({ janeUserId }),
        (data) =>
          data
            ? {
                ...data,
                bank_accounts: data.bank_accounts.filter(
                  (bankAccount) =>
                    bankAccount.bank_account_id !== variables.bankAccountId
                ),
              }
            : data
      );
    },
  });
};
